import { useEffect, useRef } from 'react';
const INTERIM_MIN_MS = 400;
function speechRecognitionCtor() {
    if (typeof window === 'undefined') {
        return null;
    }
    const host = window;
    return host.SpeechRecognition ?? host.webkitSpeechRecognition ?? null;
}
export function isSpeechCaptionsSupported() {
    return speechRecognitionCtor() !== null;
}
export function transcriptFromSpeechEvent(event) {
    const result = event.results[event.resultIndex];
    const text = result?.[0]?.transcript?.replace(/\s+/g, ' ').trim();
    if (!result || !text) {
        return null;
    }
    return { text, isFinal: result.isFinal };
}
export function useSpeechCaptions({ enabled, lang, onCaption }) {
    const onCaptionRef = useRef(onCaption);
    onCaptionRef.current = onCaption;
    const supported = isSpeechCaptionsSupported();
    useEffect(() => {
        if (!enabled || !supported) {
            return;
        }
        const Ctor = speechRecognitionCtor();
        if (!Ctor) {
            return;
        }
        const recognition = new Ctor();
        recognition.continuous = true;
        recognition.interimResults = true;
        const documentLang = typeof document !== 'undefined' ? document.documentElement.lang : '';
        recognition.lang = lang ?? (documentLang || 'en-US');
        let stopped = false;
        let lastInterimAt = 0;
        recognition.onresult = (event) => {
            const parsed = transcriptFromSpeechEvent(event);
            if (!parsed) {
                return;
            }
            if (!parsed.isFinal) {
                const now = Date.now();
                if (now - lastInterimAt < INTERIM_MIN_MS) {
                    return;
                }
                lastInterimAt = now;
            }
            onCaptionRef.current(parsed.text, parsed.isFinal);
        };
        recognition.onerror = (event) => {
            const code = 'error' in event ? String(event.error) : '';
            if (code === 'not-allowed' || code === 'service-not-allowed') {
                stopped = true;
            }
        };
        recognition.onend = () => {
            if (stopped) {
                return;
            }
            try {
                recognition.start();
            }
            catch {
                // Already started.
            }
        };
        try {
            recognition.start();
        }
        catch {
            stopped = true;
        }
        return () => {
            stopped = true;
            recognition.onresult = null;
            recognition.onerror = null;
            recognition.onend = null;
            try {
                recognition.abort();
            }
            catch {
                try {
                    recognition.stop();
                }
                catch {
                    // Ignore shutdown races.
                }
            }
        };
    }, [enabled, lang, supported]);
    return { supported };
}
//# sourceMappingURL=useSpeechCaptions.js.map